import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Validate token with backend
        const userData = await authService.getCurrentUser();
        setUser(userData.user);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      localStorage.setItem('token', response.token);
      setUser(response.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const requestOtp = async (username) => {
    try {
      await authService.requestOtp(username);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Failed to request a login code' };
    }
  };

  const loginWithOtp = async (username, code) => {
    try {
      const response = await authService.loginWithOtp(username, code);
      localStorage.setItem('token', response.token);
      setUser(response.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || 'Invalid or expired code' };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateOpenProjectConfig = async (url, apiKey, telegramBotToken, telegramChatId) => {
    try {
      await authService.updateOpenProjectConfig(url, apiKey, telegramBotToken, telegramChatId);
      // Update user info to reflect the new config
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Configuration update failed' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      // Refresh so mustChangePassword (and anything else) reflects the update
      const userData = await authService.getCurrentUser();
      setUser(userData.user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Password change failed' 
      };
    }
  };

  const createAdmin = async (username, password) => {
    try {
      await authService.createAdmin(username, password);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Admin creation failed' 
      };
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    requestOtp,
    loginWithOtp,
    logout,
    updateOpenProjectConfig,
    changePassword,
    createAdmin,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
